import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Umbrella, Target, Landmark, ArrowLeft } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteChrome";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const inr = (n) =>
  isFinite(n) ? "₹" + Math.round(n).toLocaleString("en-IN") : "—";

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Num({ value, onChange, prefix, suffix, ...rest }) {
  return (
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)}
        className={`rounded-lg ${prefix ? "pl-7" : ""} ${suffix ? "pr-9" : ""}`} {...rest} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>}
    </div>
  );
}

function ResultStat({ label, value, big, color = "#061B3A" }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`font-heading font-extrabold ${big ? "text-2xl" : "text-lg"}`} style={{ color }}>{value}</div>
    </div>
  );
}

const fvSip = (monthly, ratePct, years) => {
  const i = ratePct / 1200;
  const m = years * 12;
  if (i === 0) return monthly * m;
  return monthly * ((Math.pow(1 + i, m) - 1) / i) * (1 + i);
};
const sipForTarget = (target, ratePct, years) => {
  const i = ratePct / 1200;
  const m = years * 12;
  if (i === 0) return target / m;
  return target / (((Math.pow(1 + i, m) - 1) / i) * (1 + i));
};

function SipCalc() {
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(12);
  const fv = fvSip(+monthly, +rate, +years);
  const invested = +monthly * +years * 12;
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="space-y-5">
        <Row label="Monthly Investment"><Num value={monthly} onChange={setMonthly} prefix="₹" /></Row>
        <Row label="Investment Period (years)"><Num value={years} onChange={setYears} suffix="yr" /></Row>
        <Row label="Expected Return (p.a.)"><Num value={rate} onChange={setRate} suffix="%" /></Row>
      </div>
      <div className="grid grid-cols-1 gap-4 content-start">
        <ResultStat label="Estimated Future Value" value={inr(fv)} big color="#2E9B4B" />
        <ResultStat label="Total Invested" value={inr(invested)} />
        <ResultStat label="Estimated Gains" value={inr(fv - invested)} color="#E9A21A" />
      </div>
    </div>
  );
}

function GoalCalc() {
  const [cost, setCost] = useState(2000000);
  const [years, setYears] = useState(10);
  const [inflation, setInflation] = useState(7);
  const [rate, setRate] = useState(12);
  const future = +cost * Math.pow(1 + +inflation / 100, +years);
  const sip = sipForTarget(future, +rate, +years);
  const lumpsum = future / Math.pow(1 + +rate / 100, +years);
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="space-y-5">
        <Row label="Goal Cost (today's value)"><Num value={cost} onChange={setCost} prefix="₹" /></Row>
        <Row label="Years to Goal"><Num value={years} onChange={setYears} suffix="yr" /></Row>
        <Row label="Expected Inflation"><Num value={inflation} onChange={setInflation} suffix="%" /></Row>
        <Row label="Expected Return (p.a.)"><Num value={rate} onChange={setRate} suffix="%" /></Row>
      </div>
      <div className="grid grid-cols-1 gap-4 content-start">
        <ResultStat label="Future Cost of Goal" value={inr(future)} big color="#D64545" />
        <ResultStat label="Monthly SIP Needed" value={inr(sip)} color="#2E9B4B" />
        <ResultStat label="Or One-time Lumpsum Today" value={inr(lumpsum)} color="#E9A21A" />
      </div>
    </div>
  );
}

function RetirementCalc() {
  const [age, setAge] = useState(35);
  const [retire, setRetire] = useState(60);
  const [life, setLife] = useState(85);
  const [expense, setExpense] = useState(50000);
  const [inflation, setInflation] = useState(6);
  const [preReturn, setPreReturn] = useState(12);
  const [postReturn, setPostReturn] = useState(8);

  const yearsToRet = Math.max(0, +retire - +age);
  const retYears = Math.max(1, +life - +retire);
  const futureMonthly = +expense * Math.pow(1 + +inflation / 100, yearsToRet);
  const annualAtRet = futureMonthly * 12;
  const realReturn = (1 + +postReturn / 100) / (1 + +inflation / 100) - 1;
  const corpus = realReturn === 0
    ? annualAtRet * retYears
    : annualAtRet * (1 - Math.pow(1 + realReturn, -retYears)) / realReturn * (1 + realReturn);
  const sip = sipForTarget(corpus, +preReturn, yearsToRet);

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="grid grid-cols-2 gap-4">
        <Row label="Current Age"><Num value={age} onChange={setAge} /></Row>
        <Row label="Retirement Age"><Num value={retire} onChange={setRetire} /></Row>
        <Row label="Life Expectancy"><Num value={life} onChange={setLife} /></Row>
        <Row label="Monthly Expense (now)"><Num value={expense} onChange={setExpense} prefix="₹" /></Row>
        <Row label="Inflation"><Num value={inflation} onChange={setInflation} suffix="%" /></Row>
        <Row label="Return till Retirement"><Num value={preReturn} onChange={setPreReturn} suffix="%" /></Row>
        <Row label="Return after Retirement"><Num value={postReturn} onChange={setPostReturn} suffix="%" /></Row>
      </div>
      <div className="grid grid-cols-1 gap-4 content-start">
        <ResultStat label="Retirement Corpus Needed" value={inr(corpus)} big color="#D64545" />
        <ResultStat label="Monthly Expense at Retirement" value={inr(futureMonthly)} />
        <ResultStat label="Monthly SIP Needed Now" value={inr(sip)} color="#2E9B4B" />
      </div>
    </div>
  );
}

function NetWorthCalc() {
  const [a, setA] = useState({ cash: 200000, investments: 1000000, property: 5000000, other: 0 });
  const [l, setL] = useState({ home: 3000000, personal: 0, card: 0, other: 0 });
  const setAsset = (k, v) => setA((s) => ({ ...s, [k]: +v }));
  const setLia = (k, v) => setL((s) => ({ ...s, [k]: +v }));
  const totalA = Object.values(a).reduce((x, y) => x + (+y || 0), 0);
  const totalL = Object.values(l).reduce((x, y) => x + (+y || 0), 0);
  const net = totalA - totalL;
  return (
    <div className="grid md:grid-cols-3 gap-8">
      <div className="space-y-4">
        <div className="font-heading font-bold text-brandgreen">Assets</div>
        <Row label="Cash & Savings"><Num value={a.cash} onChange={(v) => setAsset("cash", v)} prefix="₹" /></Row>
        <Row label="Investments (MF/Stocks/FD)"><Num value={a.investments} onChange={(v) => setAsset("investments", v)} prefix="₹" /></Row>
        <Row label="Property & Real Estate"><Num value={a.property} onChange={(v) => setAsset("property", v)} prefix="₹" /></Row>
        <Row label="Other Assets"><Num value={a.other} onChange={(v) => setAsset("other", v)} prefix="₹" /></Row>
      </div>
      <div className="space-y-4">
        <div className="font-heading font-bold text-branddanger">Liabilities</div>
        <Row label="Home Loan"><Num value={l.home} onChange={(v) => setLia("home", v)} prefix="₹" /></Row>
        <Row label="Personal / Vehicle Loan"><Num value={l.personal} onChange={(v) => setLia("personal", v)} prefix="₹" /></Row>
        <Row label="Credit Card Dues"><Num value={l.card} onChange={(v) => setLia("card", v)} prefix="₹" /></Row>
        <Row label="Other Liabilities"><Num value={l.other} onChange={(v) => setLia("other", v)} prefix="₹" /></Row>
      </div>
      <div className="grid grid-cols-1 gap-4 content-start">
        <ResultStat label="Total Assets" value={inr(totalA)} color="#2E9B4B" />
        <ResultStat label="Total Liabilities" value={inr(totalL)} color="#D64545" />
        <ResultStat label="Net Worth" value={inr(net)} big color={net >= 0 ? "#061B3A" : "#D64545"} />
      </div>
    </div>
  );
}

const TABS = [
  { key: "sip", label: "SIP", icon: TrendingUp, comp: SipCalc },
  { key: "retirement", label: "Retirement", icon: Umbrella, comp: RetirementCalc },
  { key: "goal", label: "Goal Planning", icon: Target, comp: GoalCalc },
  { key: "networth", label: "Net Worth", icon: Landmark, comp: NetWorthCalc },
];

export default function Calculators() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const validTabs = ["sip", "retirement", "goal", "networth"];
  const initialTab = validTabs.includes(params.get("tab")) ? params.get("tab") : "sip";
  const [tab, setTab] = useState(initialTab);
  return (
    <div>
      <SiteHeader />
      <section className="brand-gradient relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-5xl mx-auto px-5 py-14">
          <button onClick={() => navigate(-1)} className="text-white/60 text-sm flex items-center gap-1.5 hover:text-white transition-colors duration-200 mb-4">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="text-xs tracking-[0.2em] uppercase text-gold font-bold">Free Financial Calculators</div>
          <h1 className="mt-3 font-heading font-extrabold text-white text-3xl md:text-4xl tracking-tight">
            Plan the numbers behind your goals
          </h1>
          <p className="mt-3 text-white/70 text-sm max-w-xl">
            Estimate how much to invest to close the gaps identified in your Financial Health Report. These are educational tools; actual returns are not guaranteed.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 py-12">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap gap-2 bg-transparent p-0 h-auto justify-start">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`calc-tab-${t.key}`}
                className="rounded-full border border-slate-200 data-[state=active]:bg-navy data-[state=active]:text-white px-5 py-2.5 font-heading font-semibold text-sm flex items-center gap-2">
                <t.icon size={16} /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-8">
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 card-shadow">
                <t.comp />
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
        <p className="mt-6 text-xs text-slate-400 leading-relaxed">
          Disclaimer: These calculators provide estimates for educational purposes only and do not constitute
          investment advice. Returns and inflation assumptions are illustrative and not guaranteed.
        </p>
      </div>
      <SiteFooter />
    </div>
  );
}
